import type { CountryIso3166 } from "@tfg/db/browser"
import { NOT_ENTERED_YET } from "@tfg/types/endpoints/agec"

import type { CreateBusinessPartnerRepositoryRecordDto } from "~/gen/api/types/CreateBusinessPartnerRepositoryRecordDto.ts"
import type { BusinessPartnerRepositoryPrefillResponseDto } from "~/gen/api/types/BusinessPartnerRepositoryPrefillResponseDto.ts"
import {
  businessPartnerRepositorySuggestionResponseDtoMatchClassificationEnum,
  type BusinessPartnerRepositorySuggestionResponseDto,
} from "~/gen/api/types/BusinessPartnerRepositorySuggestionResponseDto.ts"
import type {
  BusinessPartnerRepositoryAddressSnapshot,
  BusinessPartnerRepositoryPrefillSelection,
} from "~/pages/Admin/components/form.types"
import { COUNTRIES } from "~/utils/countries"

export const MIN_BP_REPOSITORY_AUTOCOMPLETE_CHARACTERS = 2

/** Max directory matches requested from the API for organization name autocomplete. */
export const MAX_BP_REPOSITORY_AUTOCOMPLETE_DIRECTORY_MATCHES = 10

/**
 * Mantine `Autocomplete` `limit`: one synthetic "create anyway" row plus
 * {@link MAX_BP_REPOSITORY_AUTOCOMPLETE_DIRECTORY_MATCHES} directory options.
 */
export const BP_REPOSITORY_AUTOCOMPLETE_DROPDOWN_LIMIT =
  1 + MAX_BP_REPOSITORY_AUTOCOMPLETE_DIRECTORY_MATCHES

export const isStrongBusinessPartnerRepositorySuggestion = (
  suggestion: BusinessPartnerRepositorySuggestionResponseDto
): boolean =>
  suggestion.matchClassification ===
  businessPartnerRepositorySuggestionResponseDtoMatchClassificationEnum.strong

export const getBusinessPartnerRepositorySuggestionSearchHaystack = (
  suggestion: BusinessPartnerRepositorySuggestionResponseDto
): string =>
  [
    suggestion.name,
    formatBusinessPartnerRepositoryAddress(suggestion),
    getBusinessPartnerRepositoryCountryLabel(suggestion.country) ?? "",
    formatBusinessPartnerRepositoryRegistrationIds(suggestion.registrationIds),
  ]
    .join(" ")
    .toLowerCase()

export const doesBusinessPartnerRepositorySuggestionMatchSearch = (
  suggestion: BusinessPartnerRepositorySuggestionResponseDto,
  search: string
): boolean => {
  const normalizedQuery = search.toLowerCase().trim()
  if (normalizedQuery.length === 0) {
    return false
  }
  return getBusinessPartnerRepositorySuggestionSearchHaystack(
    suggestion
  ).includes(normalizedQuery)
}

/** Aligns with {@link OrganizationAutocompleteHelperService.normalizeAutocompleteTerm} in core. */
export const normalizeBusinessPartnerRepositoryAutocompleteTerm = (
  value: string
): string =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")

const getNameRelevanceRankForAutocompleteTerm = (
  suggestionName: string,
  normalizedTerm: string
): number => {
  if (normalizedTerm.length === 0) {
    return 2
  }
  const normalizedName =
    normalizeBusinessPartnerRepositoryAutocompleteTerm(suggestionName)
  if (normalizedName.startsWith(normalizedTerm)) {
    return 0
  }
  if (normalizedName.includes(normalizedTerm)) {
    return 1
  }
  return 2
}

/**
 * Primary strong duplicate among directory rows visible for `search` (haystack filter),
 * ranked by name overlap with the typed term — not first strong in raw API order.
 */
export const findPrimaryVisibleStrongSuggestionForSearch = (
  suggestionsInDisplayOrder: readonly BusinessPartnerRepositorySuggestionResponseDto[],
  search: string
): BusinessPartnerRepositorySuggestionResponseDto | null => {
  const visibleStrong = suggestionsInDisplayOrder.filter(
    (suggestion) =>
      doesBusinessPartnerRepositorySuggestionMatchSearch(suggestion, search) &&
      isStrongBusinessPartnerRepositorySuggestion(suggestion)
  )
  if (visibleStrong.length === 0) {
    return null
  }
  const normalizedTerm =
    normalizeBusinessPartnerRepositoryAutocompleteTerm(search)
  const ranked = [...visibleStrong].sort((left, right) => {
    const rankDiff =
      getNameRelevanceRankForAutocompleteTerm(left.name, normalizedTerm) -
      getNameRelevanceRankForAutocompleteTerm(right.name, normalizedTerm)
    if (rankDiff !== 0) {
      return rankDiff
    }
    const byName = left.name.localeCompare(right.name)
    if (byName !== 0) {
      return byName
    }
    return left.repositoryRecordId.localeCompare(right.repositoryRecordId)
  })
  return ranked[0] ?? null
}

const isPresentAddressLine = (
  value: string | null | undefined
): value is string => {
  if (value === null || value === undefined) {
    return false
  }
  const trimmed = value.trim()
  return trimmed.length > 0 && trimmed !== NOT_ENTERED_YET
}

export const formatBusinessPartnerRepositoryAddress = (
  suggestion: Pick<
    BusinessPartnerRepositorySuggestionResponseDto,
    "street" | "city" | "postalCode"
  >,
  emptyAddressLabel = ""
): string => {
  const parts = [
    suggestion.street,
    suggestion.city,
    suggestion.postalCode,
  ].filter(isPresentAddressLine)
  return parts.length > 0 ? parts.join(", ") : emptyAddressLabel
}

const isCountryKey = (country: string): country is keyof typeof COUNTRIES => {
  return country in COUNTRIES
}

export const getBusinessPartnerRepositoryCountryLabel = (
  country: string | null
): string | null => {
  if (!country) {
    return null
  }
  if (isCountryKey(country)) {
    return COUNTRIES[country].countryName
  }
  return country
}

export const formatBusinessPartnerRepositoryRegistrationIds = (
  registrationIds: BusinessPartnerRepositorySuggestionResponseDto["registrationIds"]
): string => {
  if (registrationIds.length === 0) {
    return ""
  }
  return registrationIds
    .map(
      (registrationId) =>
        `${registrationId.type}: ${registrationId.registrationId}`
    )
    .join(" · ")
}

export const toRepositoryAddressSnapshotFromSuggestion = (
  suggestion: Pick<
    BusinessPartnerRepositorySuggestionResponseDto,
    "repositoryRecordId" | "street" | "city" | "postalCode" | "country"
  >
): BusinessPartnerRepositoryAddressSnapshot => {
  return {
    id: suggestion.repositoryRecordId,
    street: suggestion.street,
    city: suggestion.city,
    postalCode: suggestion.postalCode,
    country: suggestion.country,
  }
}

export const toCreateBusinessPartnerRepositoryRecordPayload = (
  record: BusinessPartnerRepositoryAddressSnapshot
): CreateBusinessPartnerRepositoryRecordDto => {
  return {
    id: record.id,
    street: record.street ?? undefined,
    city: record.city ?? undefined,
    postalCode: record.postalCode ?? undefined,
    country: record.country ?? undefined,
  }
}

export const toBusinessPartnerRepositoryPrefillSelectionFromPrefill = (
  organization: BusinessPartnerRepositoryPrefillResponseDto
): BusinessPartnerRepositoryPrefillSelection => {
  return {
    name: organization.name,
    repositoryRecord: {
      id: organization.repositoryRecordId,
      street: organization.street,
      city: organization.city,
      postalCode: organization.postalCode,
      country: organization.country,
    },
    lockedRegistrationIds: organization.registrationIds.map(
      (registrationId) => ({
        type: registrationId.type,
        registrationId: registrationId.registrationId,
      })
    ),
  }
}

export const toBusinessPartnerRepositoryPrefillSelectionFromSuggestion = (
  suggestion: BusinessPartnerRepositorySuggestionResponseDto
): BusinessPartnerRepositoryPrefillSelection => {
  return {
    name: suggestion.name,
    repositoryRecord: toRepositoryAddressSnapshotFromSuggestion(suggestion),
  }
}

export const toCountryIso3166OrNull = (
  country: string | null
): CountryIso3166 | null => {
  if (!country || !isCountryKey(country)) {
    return null
  }
  return country
}
